import type { KeyboardEvent, SyntheticEvent } from 'react';
import React, { useRef, useState } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { Image } from '@noths/polaris-client-ribbons-design-system';
import { getProductImageURL, isKeyboardTriggeredEvent } from '@noths/polaris-client-utils';

import { PRODUCT_IMAGES_SECTION_LABEL_PLURAL } from 'src/components/organisms/ProductImagesCarousel/constants/copy';
import { PRODUCT_IMAGES_TABPANEL_ID_PREFIX } from 'src/components/organisms/ProductImagesCarousel/constants/elementIds';
import {
  mediaKey,
  VIDEO_THUMBNAIL_WIDTH,
  videoPosterUrl,
} from 'src/components/organisms/ProductImagesCarousel/media/buildProductMedia';
import type {
  ImagesLocation,
  MediaItem,
  NextOrPreviousPayload,
  ProductImagesState,
  SetCurrentImagePayload,
} from 'src/components/organisms/ProductImagesCarousel/types';
import { useThumbnailFocusAndScroll } from './useThumbnailFocusAndScroll';
import * as styles from './Thumbnails.styles';

export interface OnVisiblePayload {
  totalImages: number;
}

export interface ThumbnailsStateProps {
  currentImage: ProductImagesState['currentImage'];
  media: MediaItem[];
}

export interface ThumbnailsDispatchProps {
  onNextImage: (payload: NextOrPreviousPayload) => void;
  onPrevImage: (payload: NextOrPreviousPayload) => void;
  onVisible: (payload: OnVisiblePayload) => void;
  setCurrentImage: (payload: SetCurrentImagePayload) => void;
}

interface ThumbnailsOwnProps {
  location: ImagesLocation;
}

type ThumbnailsProps = ThumbnailsStateProps & ThumbnailsDispatchProps & ThumbnailsOwnProps;

export const Thumbnails = ({
  currentImage,
  location,
  media,
  onNextImage,
  onPrevImage,
  onVisible,
  setCurrentImage,
}: ThumbnailsProps) => {
  const idPrefix = location.toLowerCase().replace(/\s/g, '');
  const totalImages = media.length;
  const productImagesCarouselRef = useImpressionRef<HTMLDivElement>(() => {
    onVisible({ totalImages });
  });
  const thumbnailRefs = useRef<HTMLButtonElement[] | null[]>([]);
  const [thumbnailsHaveKeyboardFocus, setThumbnailsHaveKeyboardFocus] = useState(false);
  const prevImageKey = 'ArrowLeft';
  const nextImageKey = 'ArrowRight';
  const visibilityTrackingEnabled = location !== 'Expanded image';

  useThumbnailFocusAndScroll({
    thumbnailsHaveKeyboardFocus,
    thumbnailRefs,
    currentImage,
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case prevImageKey:
        onPrevImage({ location, totalImages });
        event.preventDefault();
        break;
      case nextImageKey:
        onNextImage({ location, totalImages });
        event.preventDefault();
        break;
    }
  };

  const handleThumbnailFocus = (event: SyntheticEvent<HTMLElement>) =>
    setThumbnailsHaveKeyboardFocus(isKeyboardTriggeredEvent(event));

  const handleClick = (imageIndex: number) => {
    setCurrentImage({
      imageIndex,
      navigationType: 'click',
      location,
    });
  };

  return (
    <div
      aria-label={PRODUCT_IMAGES_SECTION_LABEL_PLURAL}
      aria-orientation="horizontal"
      css={[styles.root, thumbnailsHaveKeyboardFocus ? styles.thumbnailsWithKeyboardFocus : null]}
      onBlur={() => setThumbnailsHaveKeyboardFocus(false)}
      onFocus={handleThumbnailFocus}
      ref={visibilityTrackingEnabled ? productImagesCarouselRef : null}
      role="tablist"
    >
      <ul css={styles.list}>
        {media.map((item, i) => {
          const isVideo = item.type === 'video';
          const key = mediaKey(item);

          return (
            <li css={styles.listItem} key={key}>
              <button
                aria-controls={`${idPrefix}${PRODUCT_IMAGES_TABPANEL_ID_PREFIX}${i}`}
                aria-label={`${item.title}, ${i + 1} of ${totalImages}`}
                aria-selected={i === currentImage}
                css={styles.imageButton}
                onClick={() => handleClick(i)}
                onKeyDown={handleKeyDown}
                ref={(elem) => {
                  thumbnailRefs.current[i] = elem;
                }}
                role="tab"
                tabIndex={i === currentImage ? undefined : -1}
                type="button"
              >
                {isVideo ? (
                  <span css={styles.videoThumbnail}>
                    <img
                      alt={`${item.title}, thumbnail ${i + 1} of ${totalImages}`}
                      css={styles.videoPoster}
                      src={videoPosterUrl(item.playbackId, VIDEO_THUMBNAIL_WIDTH)}
                    />
                    <span aria-hidden="true" css={styles.playIcon}>
                      ▶
                    </span>
                  </span>
                ) : (
                  <Image
                    alt={`${item.title}, thumbnail ${i + 1} of ${totalImages}`}
                    css={styles.image}
                    src={getProductImageURL(item.href, 'mini')}
                  />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
